/**
 * Tower registry. Auto-discovers every design in src/game/designs/ via Vite's
 * import.meta.glob, validates each against the standard format, and exposes the
 * valid ones. Drop a new file in designs/ that exports `design` and it appears
 * in the game automatically — no wiring. See docs/TOWER_SPEC.md.
 */
import { validateTower, type TowerDesign } from "@/game/towerSpec";

const modules = import.meta.glob<{ design?: TowerDesign }>("./designs/*.ts", { eager: true });

export interface RegisteredDesign extends TowerDesign {
  file: string;
}

const registry: RegisteredDesign[] = [];
const seen = new Set<string>();

for (const [file, mod] of Object.entries(modules)) {
  const design = mod.design;
  if (!design) {
    console.warn(`[towers] ${file}: no exported "design" — skipped`);
    continue;
  }
  const result = validateTower(design);
  if (!result.ok) {
    console.error(`[towers] ${design.id || file}: invalid, skipped —`, result.errors);
    continue;
  }
  if (result.warnings.length) console.warn(`[towers] ${design.id}:`, result.warnings);
  if (seen.has(design.id)) {
    console.error(`[towers] duplicate id "${design.id}" in ${file} — skipped`);
    continue;
  }
  seen.add(design.id);
  registry.push({ ...design, file });
}

registry.sort((a, b) => a.name.localeCompare(b.name));

export function listDesigns(): RegisteredDesign[] {
  return registry;
}

export function getDesign(id: string): RegisteredDesign | undefined {
  return registry.find((d) => d.id === id);
}
